import { useCallback, useEffect, useRef, useState } from "react";

interface OrderRingingOptions {
	enabled: boolean;
	dings: number;
	resetKey?: string;
	immediate?: boolean;
}

export const useOrderRinging = ({
	enabled,
	dings,
	resetKey,
	immediate = false,
}: OrderRingingOptions) => {
	const [soundBlocked, setSoundBlocked] = useState(false);
	const retryRef = useRef<(() => void) | null>(null);
	const enableSound = useCallback(() => retryRef.current?.(), []);

	useEffect(() => {
		setSoundBlocked(false);
		if (!enabled || dings <= 0) return;

		let active = true;
		let playing = false;
		let blocked = false;
		let audio: HTMLAudioElement | null = null;
		let dingTimeout: ReturnType<typeof setTimeout> | undefined;
		const gestureListenerOptions = { capture: true, once: true } as const;

		const stopAudio = (player: HTMLAudioElement | null) => {
			if (!player) return;
			player.pause();
			player.currentTime = 0;
		};
		const removeGestureListeners = () => {
			document.removeEventListener("pointerdown", retrySound, true);
			document.removeEventListener("keydown", retrySound, true);
		};
		const retrySound = () => {
			removeGestureListeners();
			blocked = false;
			setSoundBlocked(false);
			ring();
		};
		const listenForGesture = () => {
			document.addEventListener(
				"pointerdown",
				retrySound,
				gestureListenerOptions,
			);
			document.addEventListener("keydown", retrySound, gestureListenerOptions);
		};

		const playDing = (index: number) => {
			if (!active) return;
			stopAudio(audio);
			const player = new Audio("/audios/ding_once.mp3");
			audio = player;
			void player.play().then(
				() => {
					if (!active) {
						stopAudio(player);
						return;
					}
					dingTimeout = setTimeout(() => {
						if (index + 1 < dings) playDing(index + 1);
						else playing = false;
					}, 850);
				},
				() => {
					if (!active) return;
					playing = false;
					blocked = true;
					setSoundBlocked(true);
					listenForGesture();
				},
			);
		};

		const ring = () => {
			if (!active || playing || blocked) return;
			playing = true;
			playDing(0);
		};
		retryRef.current = retrySound;
		const interval = setInterval(ring, 15_000);
		if (immediate) ring();

		return () => {
			active = false;
			retryRef.current = null;
			removeGestureListeners();
			clearInterval(interval);
			clearTimeout(dingTimeout);
			stopAudio(audio);
		};
	}, [enabled, dings, resetKey, immediate]);

	return { soundBlocked, enableSound };
};
